/**
 * Story `fe-be-monteur-s5d-offline-en-achtergrond` (S021.5d) §A.1 — de opslag van de offline-cache:
 * IndexedDB `carculate-fitter-cache`, een eigen dunne laag naar het voorbeeld van
 * `photo-queue/idbStore.ts` (geen runtime-dependency).
 *
 * Twee object stores: `records` (sleutel = recordsleutel) en `meta` (één rij met de generatie en
 * het eigenaar-record). Elke schrijfactie toetst BINNEN één transactie de generatie en de
 * eigenaar, zodat een tweede tabblad of een late fetch nooit gegevens van een vorige sessie
 * terugzet (A-17).
 *
 * ⚠ Het TEGENDEEL van de foto-wachtrij (PB-5, A-21): hier faalt schrijven STIL. De aanroeper
 * vangt elke fout; online merkt de monteur niets, offline volgt "niet beschikbaar".
 * ⚠ Wissen = `clear()` op de stores, NOOIT `indexedDB.deleteDatabase` (TC-SCAN-09, T-4).
 */
import type { OfflineOwner, OfflineRecord, WriteToken } from './types';

export const FITTER_OFFLINE_DB = 'carculate-fitter-cache';
const RECORDS = 'records';
const META = 'meta';
const META_ID = 'state';
const VERSION = 1;

interface MetaRow {
    id: typeof META_ID;
    generation: number;
    owner: OfflineOwner | null;
}

/** De interface van de offline-store (IndexedDB of geheugen). */
export interface FitterOfflineStore {
    /** Het schrijf-kaartje van NU (synchroon). */
    token(): WriteToken;
    /**
     * Schrijft één record, alleen als het kaartje nog geldig is (geen wissen sindsdien, zelfde
     * generatie) én de eigenaar in de store dezelfde gebruiker is. `true` = geschreven.
     */
    put(record: Omit<OfflineRecord, 'generation'>, token: WriteToken): Promise<boolean>;
    /** Eén record zoals het er staat (zonder toets; zie `reader.ts`). */
    get(key: string): Promise<OfflineRecord | null>;
    /** Alle records (voor het opschonen). */
    getAll(): Promise<OfflineRecord[]>;
    /** Eén record verwijderen (W12/W13: opschonen binnen de store-module). */
    remove(key: string): Promise<void>;
    getOwner(): Promise<OfflineOwner | null>;
    /**
     * Zet het eigenaar-record. Een ANDERE eigenaar dan er staat gaat altijd via leeg (de records
     * worden gewist en de generatie gaat omhoog): nooit gemengd.
     */
    setOwner(owner: OfflineOwner): Promise<void>;
    /** Alles leeg (records + eigenaar), generatie +1. */
    clear(): Promise<void>;
}

// ─── Gedeelde regels (geheugen én IndexedDB) ─────────────────────────────────

function tokenStillValid(token: WriteToken, localClears: number, meta: MetaRow, userId: string): boolean {
    if (token.localClears !== localClears) return false;
    if (token.generation !== null && token.generation !== meta.generation) return false;
    return meta.owner !== null && meta.owner.userId === userId;
}

const EMPTY_META: MetaRow = { id: META_ID, generation: 0, owner: null };

// ─── Geheugen (tests, of zonder IndexedDB) ───────────────────────────────────

/** Een opslag in het geheugen (tests, of als IndexedDB ontbreekt). Zelfde regels als IndexedDB. */
export function createMemoryOfflineStore(): FitterOfflineStore & { records: Map<string, OfflineRecord> } {
    const records = new Map<string, OfflineRecord>();
    let meta: MetaRow = { ...EMPTY_META };
    let localClears = 0;

    const wipe = () => {
        records.clear();
        localClears++;
        meta = { id: META_ID, generation: meta.generation + 1, owner: null };
    };

    return {
        records,
        token: () => ({ localClears, generation: meta.generation }),
        put: async (record, token) => {
            if (!tokenStillValid(token, localClears, meta, record.userId)) return false;
            records.set(record.key, { ...record, generation: meta.generation } as OfflineRecord);
            return true;
        },
        get: async (key) => records.get(key) ?? null,
        getAll: async () => [...records.values()],
        remove: async (key) => {
            records.delete(key);
        },
        getOwner: async () => meta.owner,
        setOwner: async (owner) => {
            if (meta.owner && meta.owner.userId !== owner.userId) wipe();
            meta = { ...meta, owner: { ...owner } };
        },
        clear: async () => {
            wipe();
        },
    };
}

// ─── IndexedDB ───────────────────────────────────────────────────────────────

function openDb(factory: IDBFactory, name: string): Promise<IDBDatabase> {
    return new Promise<IDBDatabase>((resolve, reject) => {
        const req = factory.open(name, VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(RECORDS)) db.createObjectStore(RECORDS, { keyPath: 'key' });
            if (!db.objectStoreNames.contains(META)) db.createObjectStore(META, { keyPath: 'id' });
        };
        req.onsuccess = () => {
            const db = req.result;
            // Een nieuwere versie in een ander tabblad wil upgraden: deze verbinding sluiten,
            // anders blijft die upgrade (en elk wachten erop) hangen. De volgende aanroep opent opnieuw.
            db.onversionchange = () => db.close();
            resolve(db);
        };
        req.onerror = () => reject(req.error ?? new Error('IndexedDB openen mislukt'));
        // Gate O-1: een ander tabblad houdt een oudere versie open. Niet blijven wachten — de
        // aanroeper faalt stil (PB-5) en een volgende poging opent opnieuw.
        req.onblocked = () => reject(new Error('IndexedDB geblokkeerd door een ander tabblad'));
    });
}

function txDone(tx: IDBTransaction): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error('IndexedDB-transactie mislukt'));
        tx.onabort = () => reject(tx.error ?? new Error('IndexedDB-transactie afgebroken'));
    });
}

function promisify<T>(req: IDBRequest<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB-fout'));
    });
}

function readMeta(raw: unknown): MetaRow {
    if (!raw || typeof raw !== 'object') return { ...EMPTY_META };
    const r = raw as Partial<MetaRow>;
    const generation = typeof r.generation === 'number' && Number.isFinite(r.generation) ? r.generation : 0;
    const o = r.owner as Partial<OfflineOwner> | null | undefined;
    const owner =
        o && typeof o.userId === 'string' && typeof o.verifiedAt === 'number'
            ? {
                  userId: o.userId,
                  displayName: typeof o.displayName === 'string' ? o.displayName : '',
                  companyName: typeof o.companyName === 'string' ? o.companyName : '',
                  verifiedAt: o.verifiedAt,
              }
            : null;
    return { id: META_ID, generation, owner };
}

/** De IndexedDB-implementatie van {@link FitterOfflineStore}. */
export function createFitterOfflineStore(factory: IDBFactory, name: string = FITTER_OFFLINE_DB): FitterOfflineStore {
    let dbPromise: Promise<IDBDatabase> | null = null;
    /** De laatst bekende generatie (null tot de database geopend is). */
    let knownGeneration: number | null = null;
    let localClears = 0;

    const db = () => {
        if (!dbPromise) {
            dbPromise = openDb(factory, name)
                .then(async (d) => {
                    // Na een versionchange is deze verbinding dicht: de volgende aanroep opent opnieuw.
                    d.onversionchange = () => {
                        d.close();
                        dbPromise = null;
                    };
                    // De generatie meteen kennen, zodat schrijf-kaartjes hem dragen.
                    const tx = d.transaction(META, 'readonly');
                    const meta = readMeta(await promisify(tx.objectStore(META).get(META_ID)));
                    knownGeneration = meta.generation;
                    return d;
                })
                .catch((e) => {
                    dbPromise = null; // een volgende poging mag het opnieuw proberen
                    throw e;
                });
        }
        return dbPromise;
    };
    // Al openen bij het aanmaken: de eerste lading van de dag moet een kaartje met generatie krijgen.
    void db().catch(() => undefined);

    /** Een schrijf-transactie over beide stores, met de meta-rij als invoer. */
    const withMeta = async (
        work: (meta: MetaRow, records: IDBObjectStore, metaStore: IDBObjectStore) => void,
    ): Promise<void> => {
        const d = await db();
        const tx = d.transaction([RECORDS, META], 'readwrite');
        const records = tx.objectStore(RECORDS);
        const metaStore = tx.objectStore(META);
        const req = metaStore.get(META_ID);
        req.onsuccess = () => {
            const meta = readMeta(req.result);
            knownGeneration = meta.generation;
            work(meta, records, metaStore);
        };
        await txDone(tx);
    };

    const wipeIn = (meta: MetaRow, records: IDBObjectStore, metaStore: IDBObjectStore, owner: OfflineOwner | null) => {
        records.clear();
        const next: MetaRow = { id: META_ID, generation: meta.generation + 1, owner };
        metaStore.put(next);
        knownGeneration = next.generation;
    };

    return {
        token: () => ({ localClears, generation: knownGeneration }),

        async put(record, token) {
            let written = false;
            const clearsAtStart = localClears;
            await withMeta((meta, records) => {
                if (clearsAtStart !== localClears) return;
                if (!tokenStillValid(token, localClears, meta, record.userId)) return;
                records.put({ ...record, generation: meta.generation });
                written = true;
            });
            return written;
        },

        async get(key) {
            const d = await db();
            const tx = d.transaction(RECORDS, 'readonly');
            const raw = await promisify(tx.objectStore(RECORDS).get(key));
            return (raw as OfflineRecord | undefined) ?? null;
        },

        async getAll() {
            const d = await db();
            const tx = d.transaction(RECORDS, 'readonly');
            return (await promisify(tx.objectStore(RECORDS).getAll())) as OfflineRecord[];
        },

        async remove(key) {
            const d = await db();
            const tx = d.transaction(RECORDS, 'readwrite');
            tx.objectStore(RECORDS).delete(key);
            await txDone(tx);
        },

        async getOwner() {
            const d = await db();
            const tx = d.transaction(META, 'readonly');
            const meta = readMeta(await promisify(tx.objectStore(META).get(META_ID)));
            knownGeneration = meta.generation;
            return meta.owner;
        },

        async setOwner(owner) {
            await withMeta((meta, records, metaStore) => {
                if (meta.owner && meta.owner.userId !== owner.userId) {
                    localClears++;
                    wipeIn(meta, records, metaStore, { ...owner });
                    return;
                }
                metaStore.put({ ...meta, owner: { ...owner } });
            });
        },

        async clear() {
            // Eerst lokaal: een kaartje van vóór dit moment is meteen ongeldig, ook als de
            // transactie hieronder nog loopt (uitloggen tijdens een lopende fetch, A-17).
            localClears++;
            await withMeta((meta, records, metaStore) => wipeIn(meta, records, metaStore, null));
        },
    };
}
